interface ConsoleLocation {
  protocol: string;
  host: string;
  search: string;
}

/**
 * Firebase Hosting is HTTPS, while the production bundle embedded by a controller is served over
 * plain HTTP. Vite's HTTP development server remains a cloud-origin fixture unless the existing
 * query override explicitly opts it into controller mode.
 */
export function isLocalConsoleOrigin(current: ConsoleLocation, productionBuild: boolean): boolean {
  const forced = new URLSearchParams(current.search).get('local-console') === '1';
  return current.protocol === 'http:' && (productionBuild || forced);
}

/**
 * Which origin serves Firebase's own auth handler (`/__/auth/**`) for this console.
 *
 * Safari partitions storage per origin, so a handler living on a DIFFERENT origin than the console
 * cannot read back the state the SDK staged for it: iOS Google sign-in dies on the handler page with
 * "Unable to process request due to missing initial state". Firebase's documented answer is to serve
 * the handler from the app's own origin, and every Firebase Hosting site already answers `/__/auth/**`
 * for its project — Hosting reserves `/__/*` ahead of our SPA `**` rewrite, so the console site serves
 * the real handler, not `index.html`.
 *
 * An HTTPS console therefore names ITSELF. That is right for `nuance-venue-{dev,prod}-console.web.app`
 * today and stays right the day the console answers on `venue.nuance.lighting`, whereas spelling out
 * `<projectId>-console.web.app` would go cross-origin again — reintroducing this exact bug — the moment
 * the custom domain lands. The origin has to be in Auth → authorized domains either way.
 *
 * Anything Firebase Hosting is NOT behind keeps the project's default handler: the plain-HTTP Vite
 * development server, and the bundle a controller serves over the cable at `http://<host>:8765`, which
 * has no Hosting to answer `/__/auth/**` and never signs anybody in — `localOrigin` stands in for a
 * signed-in user there.
 */
export function authHandlerDomain(
  current: Pick<ConsoleLocation, 'protocol' | 'host'>,
  projectId: string,
): string {
  if (current.protocol === 'https:' && current.host) return current.host;
  return `${projectId}.firebaseapp.com`;
}

export function initialLanAddress(
  current: ConsoleLocation,
  localOrigin: boolean,
  queryAddress: string | null,
  storedAddress: string | null,
): string {
  if (queryAddress) return queryAddress;
  if (localOrigin) return current.host;
  return storedAddress ?? '';
}
